<template>
  <div class="facial-recognition-container">
    <Nav v-if="!lightMode" :company="company" />
    <Spinner v-if="isLoading"></Spinner>
    <StartScreen
      v-if="showComponentStartPage()"
      @goBack="$emit('goBack')"
      v-on:start-screen-next-step="startFacialRecognition"
      :light-mode="lightMode"
      :mobile="isMobile"
    />
    <CodeQR
      v-if="showComponentQrPage() && !isMobile && !inBiometricProcess"
      :transaction-id="authTransactionId"
      :kong-token="kongToken"
      :wrapper-url="wrapperUrl"
      :trace-id="traceId"
      :company="authCompany"
      :dummyMode="dummyMode"
      :light-mode="lightMode"
      :facial-timer="checkFacialTimer()"
      :config="config"
      :kongModeJWT="kongModeJWT"
      :portalId="portalId"
      :dummyResult="dummyResult"
      @success="nextStepQr()"
    />
    <FacialResult
      v-if="showComponentErrorPage() || showComponentSuccessPage()"
      v-on:retry="retryFacialRecogniton"
      :isSuccess="isSuccess"
      :textError="textError"
      :company="authCompany"
      :retryButtonActived="retryButtonActived"
      :lightMode="lightMode"
      :isCameraBlocked="isCameraBlocked"
    />
    <div
      v-if="
        isMobile &&
        !isLoading &&
        !(showComponentErrorPage() || showComponentSuccessPage()) &&
        !windowFails &&
        !showComponentStartPage()
      "
    >
      <div class="mobile-waiting-text">Estamos esperando a que la validación facial esté completa.</div>
      <Spinner></Spinner>
    </div>
    <div v-if="!isMobile && inBiometricProcess" class="desktop-waiting-container">
      <Spinner></Spinner>
      <p class="desktop-waiting-text">
        Continúa en tu teléfono móvil, estamos esperando a que la validación facial esté completa
      </p>
    </div>

    <div v-if="windowFails" class="mobile-redirect-container">
      <Spinner></Spinner>
      <div v-if="windowFails" class="mobile-waiting-text">
        El bloqueo de ventanas emergentes de tu dispositivo está activado, si deseas continuar tu validación de
        identidad has click en el botón.
      </div>
      <input
        class="fulfillment-button"
        type="button"
        value="Continuar"
        :href="urlCode"
        onclick="window.open(this.getAttribute('href'))"
        @click="windowFails = false"
      />
    </div>
  </div>
</template>

<script>
import Spinner from "@/components/facialRecognition/Spinner";
import CodeQR from "@/components/facialRecognition/CodeQR";
import StartScreen from "@/components/facialRecognition/StartScreen";
import FacialResult from "@/components/facialRecognition/FacialResult";
import Mobile from "mobile-device-detect";
import FRConstants from "@/components/facialRecognition/constants/facialRecognitionConstant";
import { FFResultEvent } from "fulfillment-commons-js";
import Nav from "@/components/facialRecognition/Nav";
import Config from "@/utils/config";
import * as web from "@/utils/web";

let PlatformType = {
  desktop: "DESKTOP",
  mobile: "MOBILE",
};
let OSType = {
  IOS: "IOS",
  windows: "WINDOWS",
  android: "ANDROID",
  linux: "LINUX",
};

export default {
  name: "FacialRecognition",
  components: {
    StartScreen,
    CodeQR,
    FacialResult,
    Spinner,
    Nav,
  },
  props: {
    dummyMode: { type: Boolean, default: false },
    wrapperUrl: { type: String, required: true },
    company: { type: String },
    kongClient: { type: Object, required: true },
    facialRecognitionService: { type: Object, required: true },
    deviceInfoService: { type: Object },
    successCallback: { type: Function },
    failureCallback: { type: Function },
    traceId: { type: String, required: true },
    transactionId: { type: String, default: "" },
    rut: { type: String },
    mobileFlow: { type: Boolean, default: false },
    lightMode: { type: Boolean, default: false },
    facialTimer: { type: Number, default: 3 },
    config: { type: Object, required: true },
    kongModeJWT: { type: Boolean, default: false },
    portalId: { type: String },
    retryButton: { type: Boolean, default: false },
    retriesNumber: { type: Number, default: 3 },
    dummyResult: { type: String, default: FRConstants.resultTypes.success },
    dummyResponseTimeInSeconds: { type: Number, default: 3 },
    channelKey: { type: String },
  },
  data() {
    return {
      DEFAULT_TIMER: "6",
      isLoading: true,
      kongToken: undefined,
      step: "",
      isMobile: false,
      textError: FRConstants.textError.GENERIC_TEXT_ERROR,
      sessionId: "",
      authTransactionId: this.transactionId,
      authCompany: this.company,
      retryButtonActived: this.retryButton,
      retriesLeft: this.retriesNumber,
      pollingIntervalInMillis: process.env.VUE_APP_AUTH_POLLING_INTERVAL,
      pollingRetries: 0,
      tokenExpiredCode: "401",
      isSuccess: false,
      urlCode: "#",
      windowFails: false,
      inBiometricProcess: false,
      isCameraBlocked: false,
    };
  },
  created() {
    this.isLoading = false;
    this.isMobile = Mobile.isMobile;
    if (!this.isMobile && this.mobileFlow) this.isMobile = this.mobileFlow;
    this.step = FRConstants.step.START_SCREEN;
  },
  methods: {
    retryFacialRecogniton() {
      this.kongToken = undefined;
      this.textError = FRConstants.textError.GENERIC_TEXT_ERROR;
      this.sessionId = "";
      this.authTransactionId = "";
      this.retriesLeft -= 1;
      this.pollingRetries = 0;
      this.urlCode = "#";
      this.windowFails = false;
      this.inBiometricProcess = false;
      if (this.retriesLeft <= 0) this.retryButtonActived = false;
      this.step = FRConstants.step.START_SCREEN;
    },
    startFacialRecognition() {
      this.isLoading = true;
      this.step = FRConstants.step.QR_PAGE;
      if (this.rut) {
        this.facialRecognitionService
          .startValidation(
            {
              rut: this.rut,
              company: this.company,
              portalId: this.portalId,
              platformInfo: {
                platform: this.isMobile ? PlatformType.mobile : PlatformType.desktop,
                os:
                  Mobile.isMacOs || Mobile.isIOS
                    ? OSType.IOS
                    : Mobile.isWindows || Mobile.isWinPhone
                    ? OSType.windows
                    : Mobile.isAndroid
                    ? OSType.android
                    : Mobile.osName.toUpperCase().localeCompare("LINUX")
                    ? OSType.linux
                    : undefined,
              },
            },
            this.channelKey
          )
          .then((resp) => {
            this.authTransactionId = resp.transactionId;
            this.sessionId = resp.providerData.session;

            return this.kongClient.getToken();
          })
          .then((kongToken) => {
            this.kongToken = kongToken;
            this.isLoading = false;
            if (this.isMobile || this.mobileFlow) {
              this.urlCode = `${this.wrapperUrl}?transactionId=${this.authTransactionId}`
                                                + `&token=${this.kongToken}`
                                                + `&traceId=${this.traceId}`
                                                + `&tabTimer=${this.facialTimer}`
                                                + `&company=${this.company}`;

              if (this.dummyMode) {
                this.urlCode += `&dummyMode=${this.dummyMode}&dummyResult=${this.dummyResult}`;
              }
              if (this.config.environment !== Config.buildEnvironment()) {
                this.urlCode += `&env=${this.config.environment}`;
              }
              if (this.kongModeJWT) {
                this.urlCode += `&jwtMode=${this.kongModeJWT}`;
              }
              if (this.portalId) {
                this.urlCode += `&portalId=${this.portalId}`;
              }
              const finalUrl = web.sanitizeUrl(this.urlCode);
              if(!finalUrl) {
                this.nextStepError({
                  reason: "VALIDATION_ERROR",
                  message: `The url provided is not secure`,
                  source: "auth-widget",
                })
              } else {
                var newWin = window.open(this.urlCode);
                if (!newWin || newWin.closed || typeof newWin.closed === "undefined") {
                  this.windowFails = true;
                }
              }
            }
          })
          .catch((error) => {
            console.error(`failure initializing authentication session ${JSON.stringify(error)}`);
            this.retryButtonActived = false;
            this.textError = FRConstants.textError.GENERIC_TEXT_ERROR;
            if (error.message.includes(this.tokenExpiredCode)) {
              this.textError = FRConstants.textError.TOKEN_EXPIRED;
            }
            this.nextStepError(error);
          })
          .finally(() => {
            this.isLoading = false;
            if (this.dummyMode) {
              console.log(`DummyMode: Wait ${this.dummyResponseTimeInSeconds}  seconds for success response`);
              setTimeout(() => {
                if (this.dummyResult === FRConstants.resultTypes.success) {
                  this.facialRecognitionService.getStatus(this.authTransactionId, this.company).then((response) => {
                    this.nextStepSuccess(response);
                  });
                } else if (this.dummyResult === FRConstants.resultTypes.expirationError) {
                  this.textError = FRConstants.textError.TOKEN_EXPIRED;
                  this.nextStepError({
                    reason: "EXPIRED",
                    message: `Facial Recognition challenge expired`,
                    source: "auth-widget",
                  });
                } else if (this.dummyResult === FRConstants.resultTypes.unknowError) {
                  this.nextStepError({
                    reason: "REJECTED",
                    message: `Facial Recognition challenge failed`,
                    source: "auth-widget",
                  });
                } else {
                  this.nextStepError({
                    reason: "UNKNOWN_ERROR",
                    message: `Unknown error`,
                    source: "auth-widget",
                  });
                }
              }, this.dummyResponseTimeInSeconds * 1000);
            } else {
              this.pollStatus(this.authTransactionId, Date.now());
            }
          });
      } else {
        this.step = FRConstants.step.FACIAL_ERROR;
        this.isLoading = false;
      }
    },
    checkFacialTimer() {
      if (this.facialTimer < 100) return this.facialTimer.toString();
      else return this.DEFAULT_TIMER;
    },
    showComponentStartPage() {
      return !this.isLoading && this.step === FRConstants.step.START_SCREEN;
    },
    showComponentQrPage() {
      return !this.isLoading && this.step === FRConstants.step.QR_PAGE;
    },
    showComponentSuccessPage() {
      return !this.isLoading && this.step === FRConstants.step.FACIAL_SUCCESS;
    },
    showComponentErrorPage() {
      return !this.isLoading && this.step === FRConstants.step.FACIAL_ERROR;
    },
    nextStepQr() {
      this.step = FRConstants.step.ID_CARD_SELECTION;
    },
    resetBiometricProcess() {
      this.inBiometricProcess = false;
    },
    nextStepError(error) {
      window.scrollTo(0, 0);
      this.resetBiometricProcess();
      if (error.reason === FRConstants.codeError.CAMERA_NOT_AVAILABLE) {
        this.isCameraBlocked = true;
      }
      this.step = FRConstants.step.FACIAL_ERROR;
      if (error) {
        this.$emit("result", FFResultEvent.failure(error, this.traceId));
        if (typeof this.failureCallback === "function") {
          this.failureCallback(error);
        }
      }
    },
    nextStepSuccess(result) {
      window.scrollTo(0, 0);
      this.isSuccess = true;
      this.resetBiometricProcess();
      this.step = FRConstants.step.FACIAL_SUCCESS;
      this.$emit("result", FFResultEvent.success(result, this.traceId));
      if (typeof this.successCallback === "function") {
        this.successCallback(result);
      }
    },
    pollStatus(transactionId, startTime) {
      let timePassed = Date.now() - startTime;
      if (this.step === FRConstants.step.QR_PAGE) {
        if (timePassed < process.env.VUE_APP_AUTH_POLLING_MAX_TIME) {
          setTimeout(() => {
            this.facialRecognitionService
              .getStatus(transactionId, this.company)
              .then((response) => {
                this.tokenStatus = response.status;
                if (this.tokenStatus === "CREATED") {
                  this.pollStatus(transactionId, startTime);
                } else if (this.tokenStatus === "PENDING") {
                  if (response.providerData.facialRecognitionStatus === "STARTED") this.inBiometricProcess = true;
                  this.pollStatus(transactionId, startTime);
                } else if (this.tokenStatus === "APPROVED") {
                  this.nextStepSuccess(response);
                } else if (this.tokenStatus === "EXPIRED") {
                  this.textError = FRConstants.textError.TOKEN_EXPIRED;
                  this.nextStepError({
                    reason: "EXPIRED",
                    message: response.error.detail,
                    source: response.error.source,
                  });
                } else if (this.tokenStatus === "REJECTED") {
                  this.nextStepError({
                    reason: "REJECTED",
                    message: response.error.detail,
                    source: response.error.source,
                  });
                } else if (this.tokenStatus === "ERROR") {
                  if (response.error) {
                    this.nextStepError({
                      reason: response.error.code,
                      message: response.error.detail,
                      source: response.error.source,
                    });
                  } else {
                    this.nextStepError({
                      reason: "UNKNOWN_ERROR",
                      message: `Unknown error`,
                      source: "auth-widget",
                    });
                  }
                }
              })
              .catch(() => {
                this.pollingRetries += 1;
                if (this.pollingRetries <= process.env.VUE_APP_AUTH_POLLING_RETRIES) {
                  this.pollStatus(transactionId, startTime);
                } else {
                  this.nextStepError({
                    reason: "COMMUNICATION_ERROR",
                    message: `Failed to obtain facial recognition info`,
                    source: "auth-widget",
                  });
                }
              });
          }, this.pollingIntervalInMillis);
        } else {
          this.textError = FRConstants.textError.TOKEN_EXPIRED;
          this.facialRecognitionService.markExpiredStatus(
            transactionId,
            {
              code: "POLLING_EXPIRED",
              detail: `Facial Recognition challenge expired`,
              source: "auth-widget",
              portalId: this.portalId,
            },
            this.company
          );
          this.nextStepError({
            reason: "POLLING_EXPIRED",
            message: `Facial Recognition challenge expired`,
            source: "auth-widget",
          });
        }
      }
    },
  },
};
</script>
<style scoped src="@pds/core/core.min.css"></style>
<style scoped>
.facial-recognition-container {
  place-content: center;
  height: inherit;
}
.mobile-waiting-text {
  width: 100%;
  padding: 5%;
  text-align: center;
}
.mobile-redirect-text {
  width: 100%;
  padding: 5%;
  text-align: center;
  font-size: 14px;
}

.desktop-waiting-container {
  width: 380px;
  text-align: center;
  margin: auto;
  padding-top: 5%;
  color: #4a4a4a;
  font-size: 26px;
  line-height: 34px;
}

.loading-steps-container {
  padding-top: 20px;
  text-align: left;
  margin: auto;
  width: max-content;
  font-size: 18px;
}
.step-information-container {
  display: flex;
  margin-bottom: 10px;
}
.icon-container {
  padding-right: 5px;
}

.mobile-redirect-container {
  text-align: center;
}
.fulfillment-button {
  text-decoration: none;
  min-height: 44px;
  min-width: 149px;
  border-radius: 22px;
  background-color: #0076cf;
  color: white;
  padding: 1px 6px;
  font-size: 19px;
  border: 1px solid transparent;
  cursor: pointer;
  display: inline-flex;
  text-align: center;
  white-space: normal;
  align-items: center;
  justify-content: center;
  user-select: none;
  transition: all 0.2s ease;
  box-sizing: border-box;
  font-weight: 700;
  -webkit-appearance: none;
}
</style>
